// React
import { type ReactElement, useCallback, useEffect, useRef } from 'react';

// Hooks
import { useItemFetcher } from '@/hooks/use-item-fetcher';
import { useConfig } from '@salesforce/storefront-next-runtime/config';

// Components
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { useBasket, useBasketUpdater } from '@/providers/basket';
import { Spinner } from '@/components/spinner';
import type { BasketActionResponse } from '@/routes/types/action-responses';
import { useCommerceFeedback } from '@/providers/commerce-feedback-context';
import { useDataLayer } from '@/hooks/use-data-layer';
import { buildDataLayerEcommerce, buildDataLayerItemSource } from '@/lib/analytics/data-layer-mappers';

export interface RemoveItemConfig {
    action: string;
}

interface RemoveItemButtonProps {
    itemId: string;
    /** Enriched catalog + basket data used for GA4 remove_from_cart tracking */
    analyticsItemSource?: unknown;
    config?: RemoveItemConfig;
    className?: string;
}

/**
 * RemoveItemButton component that renders a remove button
 *
 * This component provides:
 * - Remove item on click (no confirmation dialog; qty-zero removal uses {@link CartQuantityPicker})
 * - Configurable messages and actions through config prop
 * - Default configuration from cart constants
 * - Loading states and error handling
 */
export function RemoveItemButton({
    itemId,
    analyticsItemSource,
    config,
    className = '',
}: RemoveItemButtonProps): ReactElement {
    const appConfig = useConfig();
    const removeAction = config?.action || appConfig.pages.cart.removeAction;
    const { t } = useTranslation('removeItem');
    const { showGeneralAlert } = useCommerceFeedback();
    const basket = useBasket();
    const updateBasket = useBasketUpdater();
    const dataLayer = useDataLayer();
    const removedItemRef = useRef<unknown>(null);
    const lastProcessedFetcherDataRef = useRef<BasketActionResponse | null>(null);
    const fetcher = useItemFetcher({
        itemId,
        componentName: 'remove-item-button',
    });
    const isLoading = fetcher.state === 'submitting';

    const removeItem = useCallback(() => {
        if (!itemId) return;

        const basketItem = basket?.productItems?.find((item) => item.itemId === itemId);
        removedItemRef.current = basketItem
            ? buildDataLayerItemSource({ ...basketItem, quantity: basketItem.quantity ?? 1 }, analyticsItemSource)
            : null;
        const formData = new FormData();
        formData.append('itemId', itemId);
        void fetcher.submit(formData, {
            method: 'POST',
            action: removeAction,
        });
    }, [analyticsItemSource, basket?.productItems, itemId, removeAction, fetcher]);

    useEffect(() => {
        if (fetcher.state !== 'idle' || !fetcher.data) {
            return;
        }
        if (fetcher.data === lastProcessedFetcherDataRef.current) {
            return;
        }
        lastProcessedFetcherDataRef.current = fetcher.data;

        if (fetcher.data.success && fetcher.data.basket) {
            if (removedItemRef.current) {
                dataLayer.track({
                    event: 'remove_from_cart',
                    ecommerce: buildDataLayerEcommerce([removedItemRef.current], {
                        currency: dataLayer.currency,
                        affiliation: dataLayer.affiliation,
                    }),
                    event_type: 'ga4_eec',
                });
            }
            removedItemRef.current = null;
            updateBasket(fetcher.data.basket);
            return;
        }

        if (!fetcher.data.success) {
            showGeneralAlert(t('failed'));
        }
    }, [dataLayer, fetcher.state, fetcher.data, t, showGeneralAlert, updateBasket]);

    const handleRemove = useCallback(() => {
        removeItem();
    }, [removeItem]);

    return (
        <Button
            variant="link"
            size="sm"
            disabled={isLoading}
            className={`text-xs cursor-pointer hover:no-underline ${className ?? ''}`}
            title={t('title')}
            data-testid={`remove-item-${itemId}`}
            aria-busy={isLoading}
            onClick={handleRemove}>
            {isLoading ? <Spinner size="sm" /> : t('button')}
        </Button>
    );
}
